<template>
  <div>
    <!-- Zona invisibile in alto a destra: 6 tap consecutivi aprono l'accesso.
         Nascosta sul pannello admin, dove coprirebbe il tasto "Esci". -->
    <div v-if="showHotCorner" class="kadmin-hotcorner" @click="registerTap" aria-hidden="true"></div>

    <transition name="kadmin-fade">
      <div v-if="phase !== 'closed'" class="kadmin-overlay" @click.self="close">
        <div class="kadmin-pin">
          <!-- Passo 1: PIN (pre-gate, se abilitato) -->
          <template v-if="phase === 'pin'">
            <h3 class="kadmin-pin__title">Accesso manutenzione</h3>
            <p class="kadmin-pin__sub">Inserisci il PIN a 6 cifre</p>
            <div class="kadmin-pin__dots">
              <span v-for="i in 6" :key="i" class="kadmin-pin__dot" :class="{ filled: digits.length >= i }"></span>
            </div>
            <p v-if="error" class="kadmin-pin__err">{{ error }}</p>
            <div class="kadmin-pin__pad">
              <button v-for="n in [1,2,3,4,5,6,7,8,9]" :key="n" class="kadmin-key" :disabled="verifying" @click="press(n)">{{ n }}</button>
              <button class="kadmin-key kadmin-key--ghost" @click="close">Annulla</button>
              <button class="kadmin-key" :disabled="verifying" @click="press(0)">0</button>
              <button class="kadmin-key kadmin-key--ghost" :disabled="verifying" @click="backspace">⌫</button>
            </div>
          </template>

          <!-- Passo 2: approvazione staff dalla dashboard (QR) -->
          <template v-else-if="phase === 'approval'">
            <h3 class="kadmin-pin__title">Approvazione staff</h3>
            <p class="kadmin-pin__sub">Inquadra il QR con il telefono e approva dalla dashboard.</p>

            <div class="kadmin-qr">
              <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR approvazione" class="kadmin-qr__img" />
              <div v-else class="kadmin-spinner-sm"></div>
            </div>

            <p v-if="approvalError" class="kadmin-pin__err">{{ approvalError }}</p>
            <p v-else class="kadmin-pin__sub">
              <span class="kadmin-dot-pulse"></span> In attesa di approvazione…
            </p>

            <div class="kadmin-actions">
              <button v-if="approvalError" class="kadmin-key kadmin-key--ghost kadmin-wide" @click="startApproval">Riprova</button>
              <button class="kadmin-key kadmin-key--ghost kadmin-wide" @click="close">Annulla</button>
            </div>
          </template>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import QRCode from 'qrcode'

const router = useRouter()
const route = useRoute()

// La hot-corner non serve (e darebbe fastidio) mentre sei già nel pannello admin.
const showHotCorner = computed(() => route.path !== '/kiosk-admin')

const TAP_WINDOW_MS = 3000
const NEEDED_TAPS = 6
const APPROVE_BASE = 'https://dashboard.elitefitness.it/kiosk-approve?code='
const POLL_MS = 2500

let taps = []
const phase = ref('closed') // closed | pin | approval
const methods = ref([])

// PIN
const digits = ref('')
const error = ref('')
const verifying = ref(false)

// Approvazione
const qrDataUrl = ref('')
const approvalError = ref('')
let pollTimer = null

function registerTap () {
  const now = Date.now()
  taps = taps.filter((t) => now - t < TAP_WINDOW_MS)
  taps.push(now)
  if (taps.length >= NEEDED_TAPS) {
    taps = []
    openGate()
  }
}

async function openGate () {
  error.value = ''
  approvalError.value = ''
  digits.value = ''
  qrDataUrl.value = ''
  // Recupera i metodi abilitati; in caso di errore procede con l'approvazione.
  try {
    const res = await window.kioskPay.adminGetMethods()
    methods.value = (res && res.ok && res.accessMethods) ? res.accessMethods : ['PIN', 'STAFF_APP']
  } catch (e) {
    methods.value = ['PIN', 'STAFF_APP']
  }
  if (methods.value.includes('PIN')) {
    phase.value = 'pin'
  } else {
    startApproval()
  }
}

function close () {
  stopPoll()
  // Se stavamo aspettando l'approvazione, chiudi la sessione backend pending
  // (altrimenti resterebbe orfana e approvabile fino alla scadenza).
  if (phase.value === 'approval' && window.kioskPay && window.kioskPay.adminSessionCancel) {
    try { window.kioskPay.adminSessionCancel() } catch (e) { /* noop */ }
  }
  phase.value = 'closed'
  digits.value = ''
  error.value = ''
  approvalError.value = ''
  qrDataUrl.value = ''
}

function backspace () { digits.value = digits.value.slice(0, -1) }

async function press (n) {
  if (verifying.value || digits.value.length >= 6) return
  error.value = ''
  digits.value += String(n)
  if (digits.value.length === 6) await verifyPin()
}

async function verifyPin () {
  if (!window.kioskPay || !window.kioskPay.adminVerifyPin) {
    error.value = 'Modulo kiosk non disponibile.'
    digits.value = ''
    return
  }
  verifying.value = true
  try {
    const res = await window.kioskPay.adminVerifyPin(digits.value)
    if (res && res.ok) {
      digits.value = ''
      startApproval()
    } else {
      error.value = (res && res.error) || 'PIN errato.'
      digits.value = ''
    }
  } catch (e) {
    error.value = 'Errore di verifica.'
    digits.value = ''
  } finally {
    verifying.value = false
  }
}

async function startApproval () {
  stopPoll()
  approvalError.value = ''
  qrDataUrl.value = ''
  phase.value = 'approval'
  try {
    const res = await window.kioskPay.adminSessionStart()
    if (!res || !res.ok || !res.code) throw new Error((res && res.error) || 'Avvio sessione fallito')
    qrDataUrl.value = await QRCode.toDataURL(APPROVE_BASE + encodeURIComponent(res.code), { width: 280, margin: 1 })
    pollTimer = setInterval(pollApproval, POLL_MS)
  } catch (e) {
    approvalError.value = e.message || 'Impossibile avviare la sessione.'
  }
}

async function pollApproval () {
  try {
    const res = await window.kioskPay.adminSessionPoll()
    if (!res || !res.ok) return
    if (res.status === 'APPROVED' && res.token) {
      stopPoll()
      try {
        sessionStorage.setItem('kioskAdminToken', res.token)
        sessionStorage.setItem('kioskAdminStaff', res.staffName || '')
        sessionStorage.setItem('kioskAdminUnlockedAt', String(Date.now()))
      } catch (e) { /* noop */ }
      phase.value = 'closed'
      router.push({ path: '/kiosk-admin' })
    } else if (res.status === 'EXPIRED') {
      stopPoll()
      approvalError.value = 'Sessione scaduta. Riprova.'
    }
  } catch (e) { /* transitorio: riprova al prossimo tick */ }
}

function stopPoll () {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null }
}

onBeforeUnmount(stopPoll)
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap');

.kadmin-hotcorner {
  position: fixed;
  top: 0;
  right: 0;
  width: 90px;
  height: 90px;
  z-index: 9997;
  background: transparent;
}

.kadmin-overlay {
  position: fixed;
  inset: 0;
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(9, 11, 40, 0.85);
  backdrop-filter: blur(4px);
  font-family: 'Montserrat', sans-serif;
}

.kadmin-pin {
  width: 100%;
  max-width: 420px;
  background: #2e348c;
  border-radius: 24px;
  padding: 32px 28px;
  text-align: center;
  color: #fff;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45);
}
.kadmin-pin__title { font-size: 1.5rem; font-weight: 700; margin: 0 0 4px; }
.kadmin-pin__sub { font-size: 1rem; opacity: 0.8; margin: 0 0 20px; }

.kadmin-pin__dots {
  display: flex;
  justify-content: center;
  gap: 14px;
  margin-bottom: 16px;
}
.kadmin-pin__dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.6);
  transition: background 0.1s ease;
}
.kadmin-pin__dot.filled { background: #ffc933; border-color: #ffc933; }

.kadmin-pin__err { color: #ff8a8a; font-weight: 600; margin: 0 0 12px; min-height: 1.2em; }

.kadmin-pin__pad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.kadmin-key {
  padding: 18px 0;
  font-size: 1.5rem;
  font-weight: 600;
  border: none;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
}
.kadmin-key:active { background: rgba(255, 255, 255, 0.24); }
.kadmin-key--ghost { background: transparent; border: 1px solid rgba(255, 255, 255, 0.35); font-size: 1.1rem; }
.kadmin-key[disabled] { opacity: 0.5; }
.kadmin-wide { grid-column: auto; }
.kadmin-actions { display: flex; gap: 10px; margin-top: 18px; }
.kadmin-actions .kadmin-key { flex: 1; padding: 14px 0; }

.kadmin-qr {
  width: 280px;
  max-width: 80vw;
  aspect-ratio: 1;
  margin: 0 auto 16px;
  background: #fff;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.kadmin-qr__img { width: 100%; height: 100%; display: block; }

.kadmin-spinner-sm {
  width: 48px; height: 48px;
  border-radius: 50%;
  border: 6px solid rgba(46, 52, 140, 0.2);
  border-top-color: #2e348c;
  animation: kadmin-spin 0.9s linear infinite;
}
@keyframes kadmin-spin { to { transform: rotate(360deg); } }

.kadmin-dot-pulse {
  display: inline-block;
  width: 10px; height: 10px;
  border-radius: 50%;
  background: #2ecc71;
  margin-right: 6px;
  animation: kadmin-pulse 1.2s ease-in-out infinite;
  vertical-align: middle;
}
@keyframes kadmin-pulse {
  0%, 100% { transform: scale(0.8); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 1; }
}

.kadmin-fade-enter-active, .kadmin-fade-leave-active { transition: opacity 0.2s ease; }
.kadmin-fade-enter-from, .kadmin-fade-leave-to { opacity: 0; }
</style>
